import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Copy, Edit3, Archive, Eye, Sparkles, ChevronRight, ChevronDown, ChevronUp, RotateCcw, ShieldCheck, Layers, FileText, MoreVertical } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { ErrorState, Skeleton } from "@/components/BookingShell";

export const CATEGORIES = [
    { value: "general_consultation",     label: "General consultation" },
    { value: "medical_history",          label: "Medical history" },
    { value: "treatment_consultation",   label: "Treatment consultation" },
    { value: "treatment_consent",        label: "Treatment consent" },
    { value: "photography_consent",      label: "Photography consent" },
    { value: "patch_test",               label: "Patch test" },
    { value: "treatment_record",         label: "Treatment record" },
    { value: "aftercare_acknowledgement",label: "Aftercare" },
    { value: "other",                    label: "Other" },
];
export const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const STATUS_STYLE = {
    draft:     "bg-white/10 border-white/25 text-white/80",
    published: "bg-emerald-500/15 border-emerald-400/40 text-emerald-200",
    inactive:  "bg-amber-500/15 border-amber-400/40 text-amber-200",
    archived:  "bg-red-500/10 border-red-400/30 text-red-200",
};

export function StaffForms() {
    const nav = useNavigate();
    const [forms, setForms] = useState(null);
    const [services, setServices] = useState([]);
    const [error, setError] = useState(null);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");     // all|draft|published|inactive|archived
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [serviceFilter, setServiceFilter] = useState("all");
    const [busy, setBusy] = useState(false);
    const [newFormOpen, setNewFormOpen] = useState(false);
    const [newName, setNewName] = useState("");
    const [newCategory, setNewCategory] = useState("general_consultation");

    const load = () => {
        setForms(null); setError(null);
        staffApi.listForms({ include_archived: true }).then((r) => setForms(r.forms || [])).catch(setError);
    };
    useEffect(load, []);
    useEffect(() => { staffApi.services().then((r) => setServices(r.services || [])).catch(() => setServices([])); }, []);

    const filtered = useMemo(() => {
        let list = forms || [];
        if (statusFilter !== "all")   list = list.filter((f) => f.status === statusFilter);
        else                          list = list.filter((f) => f.status !== "archived");
        if (categoryFilter !== "all") list = list.filter((f) => f.category === categoryFilter);
        if (q) list = list.filter((f) => (f.name || "").toLowerCase().includes(q.toLowerCase()));
        return list;
    }, [forms, statusFilter, categoryFilter, q]);

    useEffect(() => {
        if (serviceFilter === "all") return;
        setForms(null);
        staffApi.listForms({ service_id: serviceFilter, include_archived: true })
            .then((r) => setForms(r.forms || []))
            .catch(setError);
    }, [serviceFilter]);

    const create = async () => {
        if (!newName.trim()) return;
        setBusy(true);
        try {
            const r = await staffApi.createForm({ name: newName.trim(), category: newCategory });
            setNewFormOpen(false); setNewName(""); setNewCategory("general_consultation");
            nav(`/staff/forms/${r.template.id}`);
        } catch (e) { toast.error(e?.message || "Could not create form"); }
        finally { setBusy(false); }
    };

    const doDuplicate = async (id) => {
        try {
            const r = await staffApi.duplicateForm(id);
            nav(`/staff/forms/${r.template.id}`);
        } catch (e) { toast.error(e?.message || "Duplicate failed"); }
    };

    const doArchive = async (form) => {
        if (form.status === "archived") return;
        if (!window.confirm(`Archive "${form.name}"? It will be hidden from service assignment. This is reversible.`)) return;
        try { await staffApi.archiveForm(form.id); toast.success("Archived"); load(); }
        catch (e) { toast.error(e?.message || "Archive failed"); }
    };

    const doRestore = async (form) => {
        try { await staffApi.restoreForm(form.id); toast.success("Restored"); load(); }
        catch (e) { toast.error(e?.message || "Restore failed"); }
    };

    const doDelete = async (form) => {
        const subs = form.submissions_count || 0;
        if (subs > 0) { toast.error(`Cannot delete: ${subs} customer submission${subs > 1 ? "s" : ""} exist for this form.`); return; }
        if (!window.confirm(`Permanently delete "${form.name}"? This cannot be undone.`)) return;
        try { await staffApi.deleteForm(form.id); toast.success("Deleted"); load(); }
        catch (e) { toast.error(e?.message || "Delete failed"); }
    };

    const doSeed = async () => {
        setBusy(true);
        try {
            const r = await staffApi.seedStandardForms();
            const c = r.created?.length || 0;
            if (c === 0) toast.info("Standard templates are already installed.");
            else toast.success(`${c} standard template${c === 1 ? "" : "s"} added.`);
            load();
        } catch (e) { toast.error(e?.message || "Seed failed"); }
        finally { setBusy(false); }
    };

    const doSeedV2 = async () => {
        if (!window.confirm("This will archive every current template and install the new 9-category structure. Continue?")) return;
        setBusy(true);
        try {
            const r = await staffApi.seedV2Forms();
            toast.success(`${r.count} templates installed. ${r.archived} previous archived.`);
            load();
        } catch (e) { toast.error(e?.message || "Fresh seed failed"); }
        finally { setBusy(false); }
    };

    const hasAnyForms = (forms || []).length > 0;
    const showEmpty = forms && filtered.length === 0;

    return (
        <div className="space-y-5" data-testid="staff-forms">
            <header className="flex flex-wrap items-baseline gap-3 justify-between">
                <div>
                    <h1 className="font-serif font-light text-3xl md:text-4xl text-white flex items-center gap-3">
                        <Sparkles className="h-6 w-6 text-gold" /> Consultation forms
                    </h1>
                    <p className="text-sm text-white/50 mt-1">Create and manage client consultation, consent and aftercare forms.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Link to="/staff/forms/categories" data-testid="forms-manage-categories-btn"
                          className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-gold/40 text-gold text-sm hover:bg-gold/10">
                        <Layers className="h-4 w-4" /> Manage categories
                    </Link>
                    <button onClick={() => setNewFormOpen(true)} data-testid="forms-create-btn"
                            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-gold text-navy font-semibold text-sm">
                        <Plus className="h-4 w-4" /> New form
                    </button>
                </div>
            </header>

            {/* Filters — filter WITHIN the expanded categories below */}
            <div className="rounded-2xl border hairline p-3 bg-white/[0.02] space-y-3">
                <div className="flex gap-2 items-center min-w-0">
                    <Search className="h-4 w-4 text-white/40 shrink-0" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search forms"
                           data-testid="forms-search"
                           className="flex-1 min-w-0 bg-transparent text-sm text-white placeholder-white/30 focus:outline-none" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-2 gap-2">
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="forms-filter-status"
                            className="bg-navy border hairline rounded-md text-xs text-white px-2 py-2">
                        <option value="all">Active (all)</option>
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="inactive">Inactive</option>
                        <option value="archived">Archived</option>
                    </select>
                    <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} data-testid="forms-filter-service"
                            className="bg-navy border hairline rounded-md text-xs text-white px-2 py-2">
                        <option value="all">Any service</option>
                        {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </div>
            </div>

            {error && <ErrorState error={error} onRetry={load} />}
            {!forms && !error && <Skeleton className="h-40" />}

            {/* Category-first navigation — the ONLY forms list on the page. */}
            {forms && (
                <CategoryManager
                    forms={filtered}
                    allForms={forms}
                    services={services}
                    onCreateInCategory={(slug) => {
                        // Best-effort: try to preselect a matching kind. Default consultation.
                        const catValue = (slug === "consultations") ? "general_consultation"
                            : (CATEGORIES.find(c => c.value.includes(slug.replace(/-/g, "_"))) || {}).value
                              || "general_consultation";
                        setNewCategory(catValue);
                        setNewFormOpen(true);
                    }}
                    onMoveForm={async (formId, newTreatmentCategory) => {
                        try {
                            setBusy(true);
                            await staffApi.patchForm(formId, { treatment_category: newTreatmentCategory });
                            toast.success("Form moved");
                            load();
                        } catch (e) {
                            toast.error(e?.message || "Move failed");
                        } finally { setBusy(false); }
                    }}
                    onDuplicate={doDuplicate}
                    onArchive={doArchive}
                    onRestore={doRestore}
                    onDelete={doDelete}
                    searchTerm={q}
                    statusFilter={statusFilter}
                />
            )}

            {/* Empty state — first use (before any forms exist) */}
            {forms && !hasAnyForms && (
                <div className="rounded-2xl border hairline p-8 text-center space-y-4" data-testid="forms-empty-first">
                    <ShieldCheck className="h-8 w-8 mx-auto text-gold" strokeWidth={1.4} />
                    <div>
                        <p className="font-serif text-white text-2xl">Get started</p>
                        <p className="text-sm text-white/60 mt-2 max-w-xl mx-auto">
                            You don't have any forms yet. Create your first consultation, consent or aftercare form and assign it to a treatment category.
                        </p>
                    </div>
                    <div className="flex justify-center gap-2 flex-wrap">
                        <button onClick={() => setNewFormOpen(true)}
                                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-gold text-navy font-semibold text-sm">
                            <Plus className="h-4 w-4" /> Create your first form
                        </button>
                    </div>
                </div>
            )}


            {/* Create dialog */}
            {newFormOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setNewFormOpen(false)}>
                    <div onClick={(e) => e.stopPropagation()} data-testid="forms-create-modal"
                         className="w-full sm:max-w-md m-4 rounded-2xl glass-strong border hairline p-5 space-y-3">
                        <h3 className="font-serif text-white text-xl">Create a new form</h3>
                        <label className="text-[10px] uppercase tracking-[0.18em] text-white/50 block">Form name</label>
                        <input value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus
                               placeholder="e.g. Lip Filler Consent" data-testid="forms-create-name"
                               className="w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm" />
                        <label className="text-[10px] uppercase tracking-[0.18em] text-white/50 block">Category</label>
                        <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)} data-testid="forms-create-category"
                                className="w-full h-11 bg-navy border hairline rounded-md text-sm text-white px-3">
                            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                        <div className="flex gap-2 pt-2">
                            <button onClick={() => setNewFormOpen(false)}
                                    className="flex-1 h-11 rounded-full border hairline text-white/80 text-sm">Cancel</button>
                            <button onClick={create} disabled={busy || !newName.trim()} data-testid="forms-create-submit"
                                    className="flex-1 h-11 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-40 inline-flex items-center justify-center gap-1">
                                Continue <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default StaffForms;


const TREATMENT_CATEGORIES = [
    { slug: "consultations",       label: "Consultations" },
    { slug: "anti-wrinkle",        label: "Anti Wrinkle" },
    { slug: "dermal-fillers",      label: "Dermal Fillers" },
    { slug: "skin-boosters",       label: "Skin Boosters / Polynucleotides" },
    { slug: "fat-dissolving",      label: "Fat Dissolving" },
    { slug: "facials",             label: "Facials / Dermaplaning" },
    { slug: "microneedling",       label: "Microneedling / Mesotherapy" },
    { slug: "vitamin-injections",  label: "Vitamin Injections" },
    { slug: "hair-filler",         label: "Hair Filler" },
];

const KIND_LABEL = {
    consultation: "Consultation",
    medical:      "Medical Form",
    aftercare:    "Aftercare",
};

// Canonical form ordering within a category — matches Angela's request:
// Consultation → Medical Form → Aftercare, then anything else after.
const KIND_ORDER = { consultation: 0, medical: 1, aftercare: 2 };
const kindSort = (a, b) =>
    (KIND_ORDER[a.form_kind] ?? 99) - (KIND_ORDER[b.form_kind] ?? 99)
    || (a.name || "").localeCompare(b.name || "");

function CategoryManager({
    forms, allForms, services, searchTerm, statusFilter,
    onCreateInCategory, onMoveForm, onDuplicate, onArchive, onRestore, onDelete,
}) {
    const [openMap, setOpenMap] = React.useState(() => {
        try { return JSON.parse(localStorage.getItem("pout-forms-open") || "{}"); }
        catch { return {}; }
    });
    const toggle = (slug) => setOpenMap((m) => {
        const next = { ...m, [slug]: !m[slug] };
        try { localStorage.setItem("pout-forms-open", JSON.stringify(next)); } catch {}
        return next;
    });

    // Bucket every form by treatment_category. Uncategorised forms roll up into
    // an "Uncategorised" section so Angela can move them.
    const byCat = React.useMemo(() => {
        const map = {};
        (allForms || []).forEach((f) => {
            const c = f.treatment_category || "uncategorised";
            (map[c] ||= []).push(f);
        });
        return map;
    }, [allForms]);

    // filtered subset for showing (respects search + status filter).
    const filteredIds = React.useMemo(() => new Set((forms || []).map((f) => f.id)), [forms]);
    const hasActiveFilter = Boolean(searchTerm) || statusFilter !== "all";

    // Auto-expand any category that contains a filter match so the user can see results.
    React.useEffect(() => {
        if (!hasActiveFilter) return;
        const next = {};
        Object.entries(byCat).forEach(([slug, list]) => {
            if (list.some((f) => filteredIds.has(f.id))) next[slug] = true;
        });
        setOpenMap((m) => ({ ...m, ...next }));
    }, [searchTerm, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // Draw categories in canonical order, then "Uncategorised" last if it has forms.
    const orderedSlugs = [
        ...TREATMENT_CATEGORIES.map((c) => c.slug),
        ...(byCat["uncategorised"] ? ["uncategorised"] : []),
    ];
    const labelForSlug = (slug) => {
        if (slug === "uncategorised") return "Uncategorised";
        return (TREATMENT_CATEGORIES.find((c) => c.slug === slug) || {}).label || slug;
    };

    return (
        <section className="space-y-3" data-testid="forms-by-category">
            <div className="grid gap-3 md:grid-cols-2">
                {orderedSlugs.map((slug) => {
                    const list = (byCat[slug] || []).filter((f) => filteredIds.has(f.id));
                    // Skip empty categories entirely when a filter is active — the
                    // user is looking for something specific.
                    if (hasActiveFilter && list.length === 0) return null;
                    const total = (byCat[slug] || []).length;
                    const isOpen = !!openMap[slug];
                    return (
                        <div key={slug}
                             className="rounded-2xl bg-[#0E1746] surface-highlight shadow-lg overflow-hidden"
                             data-testid={`fc-${slug}`}>
                            <button type="button" onClick={() => toggle(slug)}
                                    aria-expanded={isOpen}
                                    data-testid={`fc-${slug}-toggle`}
                                    className="w-full flex items-center justify-between gap-3 px-4 py-4 text-left hover:bg-white/[0.05] transition-colors">
                                <div className="min-w-0 flex-1">
                                    <div className="text-white font-semibold tracking-tight text-xl md:text-[22px] truncate">
                                        {labelForSlug(slug)}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mt-1">
                                        {total} form{total === 1 ? "" : "s"}
                                        {hasActiveFilter && list.length !== total && (
                                            <span className="text-gold/90"> · {list.length} match</span>
                                        )}
                                    </div>
                                </div>
                                {isOpen ? <ChevronUp className="h-5 w-5 text-gold shrink-0" /> : <ChevronRight className="h-5 w-5 text-gold shrink-0" />}
                            </button>
                            {isOpen && (
                                <div className="px-3 pb-3 pt-1 space-y-3">
                                    {list.length === 0 ? (
                                        <div className="text-center py-6 space-y-3" data-testid={`fc-${slug}-empty`}>
                                            <p className="text-sm text-white/70">No forms in this category.</p>
                                            <button
                                                onClick={() => onCreateInCategory(slug)}
                                                className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-gold text-navy font-semibold text-xs"
                                                data-testid={`fc-${slug}-create`}>
                                                <Plus className="h-3.5 w-3.5" /> Create form here
                                            </button>
                                        </div>
                                    ) : [...list].sort(kindSort).map((f) => (
                                        <FormRow key={f.id} form={f} services={services}
                                                 currentSlug={slug}
                                                 onMove={onMoveForm}
                                                 onDuplicate={onDuplicate}
                                                 onArchive={onArchive}
                                                 onRestore={onRestore}
                                                 onDelete={onDelete} />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

export function FormRow({ form: f, currentSlug, onMove, onDuplicate, onArchive, onRestore, onDelete }) {
    const nav = useNavigate();
    const [menuOpen, setMenuOpen] = React.useState(false);
    const [moveOpen, setMoveOpen] = React.useState(false);
    const menuRef = React.useRef(null);
    // Close overflow menu on outside click.
    React.useEffect(() => {
        if (!menuOpen && !moveOpen) return;
        const onDoc = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false); setMoveOpen(false);
            }
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [menuOpen, moveOpen]);

    const openBuilder = () => nav(`/staff/forms/${f.id}`);
    const isArchived = f.status === "archived";
    const kindLabel = KIND_LABEL[f.form_kind] || CAT_LABEL[f.category] || f.category;

    return (
        <div
            data-testid={`form-card-${f.id}`}
            className={`relative rounded-xl bg-white text-navy shadow-sm ${isArchived ? "opacity-60" : ""}`}
        >
            {/* Card body — the whole row is clickable to open the editor. */}
            <button
                type="button"
                onClick={openBuilder}
                data-testid={`form-open-${f.id}`}
                className="w-full text-left flex items-start gap-3 p-4 hover:bg-slate-50/70 rounded-xl transition-colors"
            >
                <span className="mt-0.5 shrink-0 h-9 w-9 rounded-lg bg-gold/10 text-gold flex items-center justify-center">
                    <FileText className="h-5 w-5" strokeWidth={1.6} />
                </span>
                <div className="min-w-0 flex-1">
                    <div className="text-navy font-semibold text-[15px] leading-snug break-words pr-16">
                        {f.name || "Untitled"}
                    </div>
                    <div className="mt-1.5 flex items-center flex-wrap gap-2">
                        {f.seed_key && (
                            <span className="inline-flex text-[10px] font-semibold tracking-[0.14em] uppercase px-2 py-0.5 rounded-full border border-gold/60 text-gold bg-gold/[0.06]">
                                Standard
                            </span>
                        )}
                        <span className="text-[12px] text-slate-500 font-medium">
                            {kindLabel}
                        </span>
                    </div>
                </div>
                {/* Status pill + chevron */}
                <div className="flex items-center gap-2 shrink-0">
                    {f.status === "published" && (
                        <span className="hidden sm:inline-flex text-[10px] font-semibold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border border-emerald-500/50 text-emerald-600 bg-emerald-500/[0.08]">
                            Published
                        </span>
                    )}
                    {f.status === "draft" && (
                        <span className="hidden sm:inline-flex text-[10px] font-semibold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border border-slate-400/50 text-slate-600 bg-slate-500/[0.06]">
                            Draft
                        </span>
                    )}
                    {f.status === "inactive" && (
                        <span className="hidden sm:inline-flex text-[10px] font-semibold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border border-amber-500/50 text-amber-600 bg-amber-500/[0.08]">
                            Inactive
                        </span>
                    )}
                    {isArchived && (
                        <span className="hidden sm:inline-flex text-[10px] font-semibold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border border-red-400/50 text-red-600 bg-red-500/[0.08]">
                            Archived
                        </span>
                    )}
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                </div>
            </button>

            <p className="px-4 pb-4 text-xs text-slate-600">Assign this form and choose when to send it in <Link to="/staff/services" className="underline">Services → Forms</Link>.</p>
            {/* Overflow menu (⋮) — positioned absolutely so it doesn't steal the row click */}
            <div ref={menuRef} className="absolute top-2 right-2">
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); setMoveOpen(false); }}
                    data-testid={`form-menu-${f.id}`}
                    aria-label="More actions"
                    className="h-8 w-8 inline-flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-navy"
                >
                    <MoreVertical className="h-4 w-4" />
                </button>
                {menuOpen && !moveOpen && (
                    <div
                        data-testid={`form-menu-panel-${f.id}`}
                        className="absolute z-20 right-0 mt-1 w-48 rounded-lg bg-white shadow-xl border border-slate-200 py-1 text-left"
                    >
                        {!isArchived ? (
                            <>
                                <MenuItem
                                    testId={`form-menu-edit-${f.id}`}
                                    onClick={() => { setMenuOpen(false); openBuilder(); }}
                                    icon={<Edit3 className="h-3.5 w-3.5" />}
                                >Edit</MenuItem>
                                <MenuItem
                                    testId={`form-menu-duplicate-${f.id}`}
                                    onClick={() => { setMenuOpen(false); onDuplicate(f.id); }}
                                    icon={<Copy className="h-3.5 w-3.5" />}
                                >Duplicate</MenuItem>
                                <MenuItem
                                    testId={`form-menu-move-${f.id}`}
                                    onClick={() => { setMoveOpen(true); }}
                                    icon={<ChevronRight className="h-3.5 w-3.5" />}
                                >Move to…</MenuItem>
                                <div className="my-1 border-t border-slate-100" />
                                <MenuItem
                                    testId={`form-menu-archive-${f.id}`}
                                    onClick={() => { setMenuOpen(false); onArchive(f); }}
                                    icon={<Archive className="h-3.5 w-3.5" />}
                                    danger
                                >Archive</MenuItem>
                            </>
                        ) : (
                            <>
                                <MenuItem
                                    testId={`form-menu-restore-${f.id}`}
                                    onClick={() => { setMenuOpen(false); onRestore(f); }}
                                    icon={<RotateCcw className="h-3.5 w-3.5" />}
                                >Restore</MenuItem>
                                {(f.submissions_count || 0) === 0 && (
                                    <MenuItem
                                        testId={`form-menu-delete-${f.id}`}
                                        onClick={() => { setMenuOpen(false); onDelete(f); }}
                                        icon={<Archive className="h-3.5 w-3.5" />}
                                        danger
                                    >Delete permanently</MenuItem>
                                )}
                            </>
                        )}
                    </div>
                )}
                {moveOpen && (
                    <div
                        data-testid={`form-move-menu-${f.id}`}
                        className="absolute z-20 right-0 mt-1 w-56 rounded-lg bg-white shadow-xl border border-slate-200 py-1 max-h-72 overflow-auto"
                    >
                        <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
                            Move to…
                        </div>
                        {TREATMENT_CATEGORIES.filter((c) => c.slug !== currentSlug).map((c) => (
                            <button
                                key={c.slug}
                                onClick={() => { setMoveOpen(false); setMenuOpen(false); onMove(f.id, c.slug); }}
                                data-testid={`form-move-${f.id}-${c.slug}`}
                                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function MenuItem({ children, onClick, icon, testId, danger }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm hover:bg-slate-50 ${
                danger ? "text-red-600 hover:bg-red-50" : "text-slate-700"
            }`}
        >
            <span className={danger ? "text-red-500" : "text-slate-400"}>{icon}</span>
            {children}
        </button>
    );
}

function TreatmentGroupedView() { return null; } // retained export shim, unused
